import { z } from 'zod';
import { emailSchema, passwordSchema } from './auth.js';
import { dateReelleSchema } from './comprehension.js';

export const timezoneSchema = z.string().refine(
  (v) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: v });
      return true;
    } catch {
      return false;
    }
  },
  { message: 'Fuseau horaire inconnu (exemples : Europe/Paris, Africa/Dakar)' },
);

export const changeTimezoneSchema = z.object({ timezone: timezoneSchema });

export const changeCitySchema = z.object({
  city: z.string().trim().min(1, 'La ville est obligatoire').max(100, 'Le nom est trop long'),
});

export const createHouseholdSchema = z.object({
  name: z.string().trim().min(1, 'Le nom du foyer est obligatoire').max(100, 'Le nom est trop long'),
  timezone: timezoneSchema.optional(),
});

/**
 * Date de naissance, optionnelle partout ou elle apparait.
 *
 * C'est une date qu'on stocke, jamais un age : un age se perime tout seul, une date de
 * naissance se recalcule. Elle sert a Colette pour adapter ses propositions — on ne
 * prepare pas la meme sortie pour un enfant de 3 ans et pour un adolescent de 16 ans.
 */
export const birthDateSchema = dateReelleSchema
  // L'existence du jour est jugee par `dateReelleSchema`, la regle de tout le projet :
  // elle vivait ici seule, recopiee nulle part, et les autres dates ne l'avaient pas.
  .refine((v) => v <= new Date().toISOString().slice(0, 10), {
    message: 'Une date de naissance ne peut pas etre dans le futur',
  });

export const addMemberSchema = z
  .object({
    displayName: z.string().trim().min(1, 'Le nom est obligatoire').max(100, 'Le nom est trop long'),
    role: z.enum(['ADULT', 'CHILD'], { message: 'Role invalide (ADULT ou CHILD)' }),
    birthDate: birthDateSchema.optional(),
    email: emailSchema.optional(),
    password: passwordSchema.optional(),
  })
  .refine((v) => (v.email === undefined) === (v.password === undefined), {
    message: 'Email et mot de passe vont ensemble pour creer un compte',
    path: ['password'],
  });

export const addPersonSchema = z.object({
  displayName: z.string().trim().min(1, 'Le nom est obligatoire').max(100, 'Le nom est trop long'),
  birthDate: birthDateSchema.optional(),
});

/**
 * Modification d'une fiche (personne ou membre) : chaque champ est optionnel, mais un
 * corps sans aucun champ est refuse — un PATCH vide est toujours une erreur d'appel.
 * `birthDate: null` EFFACE la date ; absent, il ne la touche pas.
 */
/**
 * Les identifiants d'un compte ENFANT, changes par un adulte du foyer.
 *
 * La porte de secours quand l'adresse a ete mal tapee a la creation : sans elle, le compte
 * est cree, inutilisable et irreparable (constate le 2026-08-27). Reserve aux comptes
 * enfants — un adulte ne prend jamais la main sur le compte d'un autre adulte.
 */
export const identifiantsEnfantSchema = z
  .object({
    email: emailSchema.optional(),
    password: passwordSchema.optional(),
  })
  .refine((valeur) => valeur.email !== undefined || valeur.password !== undefined, {
    message: 'Indique une nouvelle adresse email ou un nouveau mot de passe',
  });

export type IdentifiantsEnfant = z.infer<typeof identifiantsEnfantSchema>;

export const updatePersonSchema = z
  .object({
    displayName: z
      .string()
      .trim()
      .min(1, 'Le nom est obligatoire')
      .max(100, 'Le nom est trop long')
      .optional(),
    birthDate: birthDateSchema.nullable().optional(),
  })
  .refine((v) => v.displayName !== undefined || v.birthDate !== undefined, {
    message: 'Rien a modifier',
  });

export type CreateHouseholdInput = z.infer<typeof createHouseholdSchema>;
export type AddMemberInput = z.infer<typeof addMemberSchema>;
export type AddPersonInput = z.infer<typeof addPersonSchema>;
export type UpdatePersonInput = z.infer<typeof updatePersonSchema>;
export type ChangeTimezoneInput = z.infer<typeof changeTimezoneSchema>;
export type ChangeCityInput = z.infer<typeof changeCitySchema>;

/**
 * Age revolu en annees, a partir de deux jours calendaires AAAA-MM-JJ.
 *
 * Arithmetique de CHAINES, volontairement : passer par Date reintroduirait un fuseau
 * dans un calcul qui n'en a pas — une naissance est un jour pur. La comparaison
 * lexicographique de « MM-JJ » suffit a savoir si l'anniversaire est passe.
 *
 * Vit dans `shared` parce que l'API (prompt du cerveau) et l'application (affichage)
 * doivent compter EXACTEMENT pareil.
 */
export function ageEnAnnees(naissance: string, reference: string): number {
  const anneeNaissance = Number(naissance.slice(0, 4));
  const anneeReference = Number(reference.slice(0, 4));
  const anniversairePasse = reference.slice(5) >= naissance.slice(5);
  return anneeReference - anneeNaissance - (anniversairePasse ? 0 : 1);
}
